import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getCurrentWorkOrder } from '@app/core/store/selectors/work-order.selector';
import { LoadWorkOrder } from '@dwo/actions';
import { NetworkStatusService } from '@app/core/services/network-status.service';
import { UserService } from '@app/core/services/user.service';
import { ProjectControllerService } from '@app/generated/api/projectController.service';
import {
  WeightChangeCategoryDto,
  WeightLineRequest,
  WeightReportAttachmentDto,
  WeightReportLineDto,
  WeightReportSchemaDto,
  WorkOrderDetailsDto,
} from '@app/generated/model';
import { WeightReportService } from './weight-report.service';
import { formatFileSize } from '@app/utils';

@Component({
  selector: 'app-weight-report',
  templateUrl: './weight-report.component.html',
  styleUrls: ['./weight-report.component.scss'],
  standalone: false,
})
export class WeightReportComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  readonly mainCategoryEnum = WeightLineRequest.MainCategoryEnum;
  readonly schemaStatusEnum = WeightReportSchemaDto.StatusEnum;

  workOrder: WorkOrderDetailsDto | null = null;
  categories: WeightChangeCategoryDto[] = [];
  openSchema: WeightReportSchemaDto | null = null;
  sentSchemas: WeightReportSchemaDto[] = [];
  lineAttachments: Record<string, WeightReportAttachmentDto[]> = {};
  loadingAttachmentLineIds = new Set<string>();
  uploadingAttachmentLineIds = new Set<string>();

  recipientEmail = '';
  currentUserEmail = '';
  isOnline = navigator.onLine;
  hasPendingLineChanges = false;

  showLineForm = false;
  editingLine: WeightReportLineDto | null = null;
  savingLine = false;
  sending = false;
  /** Files picked before a new line has been saved; uploaded right after the line is created. */
  pendingAttachmentFiles: File[] = [];

  readonly selectedCategoryIds = new Set<string>();

  form = {
    mainCategory: WeightLineRequest.MainCategoryEnum.AdditionalWork as WeightLineRequest.MainCategoryEnum,
    areaTagSystem: '',
    quantityMetres: '',
    hoursSpent: null as number | null,
    installedWeight: null as number | null,
    removedWeight: null as number | null,
    changeFollowUpRequired: false,
    description: '',
    comment: '',
  };

  constructor(
    private readonly weightReportService: WeightReportService,
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
    private readonly networkStatusService: NetworkStatusService,
    private readonly userService: UserService,
    private readonly projectController: ProjectControllerService,
  ) {}

  ngOnInit(): void {
    const workOrderId =
      this.route.parent?.parent?.snapshot.paramMap.get('id') ?? this.route.snapshot.paramMap.get('id') ?? '';

    this.store
      .select(getCurrentWorkOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe((workOrder) => {
        if (!workOrder) {
          this.store.dispatch(new LoadWorkOrder(workOrderId));
          return;
        }
        this.workOrder = workOrder;
        if (workOrder.projectId) {
          this.loadCategories(workOrder.projectId);
          this.resolveRecipient(workOrder.projectId);
        }
        if (workOrder.id) {
          void this.refresh();
        }
      });

    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentUserEmail = (user?.email ?? user?.username ?? '').toString().trim();
      });

    this.networkStatusService
      .isOnline()
      .pipe(takeUntil(this.destroy$))
      .subscribe((online) => {
        this.isOnline = online;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleCategory(categoryId: string): void {
    if (this.selectedCategoryIds.has(categoryId)) {
      this.selectedCategoryIds.delete(categoryId);
    } else {
      this.selectedCategoryIds.add(categoryId);
    }
  }

  isCategorySelected(categoryId: string): boolean {
    return this.selectedCategoryIds.has(categoryId);
  }

  get openLines(): WeightReportLineDto[] {
    return this.openSchema?.lines ?? [];
  }

  get unsentLineCount(): number {
    return this.openLines.length;
  }

  get totalWeight(): number {
    return (this.form.installedWeight ?? 0) + (this.form.removedWeight ?? 0);
  }

  get totalWeightDisplay(): string {
    return `${this.totalWeight}kg`;
  }

  get canSend(): boolean {
    return this.isOnline && !this.sending && !this.hasPendingLineChanges && this.unsentLineCount > 0;
  }

  lineTotalWeight(line: WeightReportLineDto): number {
    return line.totalWeight ?? (line.installedWeight ?? 0) + (line.removedWeight ?? 0);
  }

  formatWeight(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '-';
    }

    return `${value}kg`;
  }

  formatHours(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '-';
    }

    return `${value}h`;
  }

  categoryLabels(line: WeightReportLineDto): string {
    return (line.selectedCategories ?? []).map((category) => category.label).join(', ');
  }

  addLine(): void {
    this.editingLine = null;
    this.resetForm();
    this.showLineForm = true;
  }

  editLine(line: WeightReportLineDto): void {
    this.editingLine = line;
    this.pendingAttachmentFiles = [];
    this.form = {
      mainCategory: line.mainCategory ?? WeightLineRequest.MainCategoryEnum.AdditionalWork,
      areaTagSystem: line.areaTagSystem ?? '',
      quantityMetres: line.quantityMetres ?? '',
      hoursSpent: line.hoursSpent ?? null,
      installedWeight: line.installedWeight ?? null,
      removedWeight: line.removedWeight ?? null,
      changeFollowUpRequired: line.changeFollowUpRequired ?? false,
      description: line.description ?? '',
      comment: line.comment ?? '',
    };
    this.selectedCategoryIds.clear();
    (line.selectedCategories ?? []).forEach((category) => {
      if (category.id) {
        this.selectedCategoryIds.add(category.id);
      }
    });
    this.showLineForm = true;

    if (line.id && this.openSchema?.id && !this.lineAttachments[line.id]) {
      this.loadLineAttachments(this.openSchema.id, line);
    }
  }

  duplicateLine(line: WeightReportLineDto): void {
    this.editLine(line);
    this.editingLine = null;
  }

  cancelLineForm(): void {
    this.showLineForm = false;
    this.editingLine = null;
    this.resetForm();
  }

  async saveLine(): Promise<void> {
    const workOrderId = this.workOrder?.id ?? '';
    if (!workOrderId || this.savingLine) {
      return;
    }

    this.savingLine = true;
    try {
      const schemaId = await this.resolveOpenSchemaId();
      const request = this.buildLineRequest();

      if (this.editingLine) {
        await this.weightReportService.updateLine(workOrderId, schemaId, this.editingLine, request);
      } else {
        const createdLine = await this.weightReportService.addLine(workOrderId, schemaId, request);
        if (createdLine?.id && this.pendingAttachmentFiles.length) {
          await this.uploadPendingAttachments(workOrderId, schemaId, createdLine.id);
        }
      }

      this.cancelLineForm();
      await this.refresh();
    } catch {
      this.snackBar.open(this.translate.instant('weightReport.lineSaveError'), 'OK', { duration: 5000 });
    } finally {
      this.savingLine = false;
    }
  }

  async deleteLine(line: WeightReportLineDto): Promise<void> {
    const workOrderId = this.workOrder?.id ?? '';
    if (!workOrderId) {
      return;
    }

    try {
      await this.weightReportService.deleteLine(workOrderId, this.openSchema?.id ?? '', line);
      await this.refresh();
    } catch {
      this.snackBar.open(this.translate.instant('weightReport.lineDeleteError'), 'OK', { duration: 5000 });
    }
  }

  async send(): Promise<void> {
    const workOrderId = this.workOrder?.id ?? '';
    const schemaId = this.openSchema?.id ?? '';
    if (!workOrderId || !schemaId || !this.canSend) {
      return;
    }

    this.sending = true;
    try {
      await this.weightReportService.sendSchema(workOrderId, schemaId);
      this.snackBar.open(this.translate.instant('weightReport.sendSuccess'), 'OK', { duration: 4000 });
      await this.refresh();
    } catch {
      this.snackBar.open(this.translate.instant('weightReport.sendError'), 'OK', { duration: 5000 });
    } finally {
      this.sending = false;
    }
  }

  resetForm(): void {
    this.form = {
      mainCategory: WeightLineRequest.MainCategoryEnum.AdditionalWork,
      areaTagSystem: '',
      quantityMetres: '',
      hoursSpent: null,
      installedWeight: null,
      removedWeight: null,
      changeFollowUpRequired: false,
      description: '',
      comment: '',
    };
    this.selectedCategoryIds.clear();
    this.pendingAttachmentFiles = [];
  }

  onPendingFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';

    this.pendingAttachmentFiles = [...this.pendingAttachmentFiles, ...files];
  }

  removePendingAttachment(file: File): void {
    this.pendingAttachmentFiles = this.pendingAttachmentFiles.filter((pending) => pending !== file);
  }

  formatFileSize(bytes?: number): string {
    return formatFileSize(bytes);
  }

  getLineAttachmentsList(lineId?: string): WeightReportAttachmentDto[] {
    if (!lineId) {
      return [];
    }

    return this.lineAttachments[lineId] ?? [];
  }

  hasLineAttachments(lineId?: string): boolean {
    return this.getLineAttachmentsList(lineId).length > 0;
  }

  isLoadingLineAttachments(lineId?: string): boolean {
    return !!lineId && this.loadingAttachmentLineIds.has(lineId);
  }

  isUploadingLineAttachments(lineId?: string): boolean {
    return !!lineId && this.uploadingAttachmentLineIds.has(lineId);
  }

  canUploadLineAttachments(line: WeightReportLineDto | null): boolean {
    return !!line?.id && this.isOnline;
  }

  onSchemaPanelOpened(schema: WeightReportSchemaDto): void {
    this.loadLineAttachmentsForSchema(schema);
  }

  onLineFilesSelected(line: WeightReportLineDto, event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';

    const workOrderId = this.workOrder?.id ?? '';
    const schemaId = this.openSchema?.id ?? '';
    const lineId = line?.id ?? '';
    if (!this.canUploadLineAttachments(line) || !workOrderId || !schemaId || !lineId || !files.length) {
      return;
    }

    this.uploadingAttachmentLineIds.add(lineId);
    const uploads = files.map((file) =>
      firstValueFrom(this.weightReportService.uploadLineAttachment(workOrderId, schemaId, lineId, file)),
    );

    Promise.all(uploads)
      .then(() => {
        this.loadLineAttachments(schemaId, line);
      })
      .catch(() => {
        this.snackBar.open(this.translate.instant('weightReport.documentation.attachmentUploadError'), 'OK', {
          duration: 5000,
        });
      })
      .finally(() => {
        this.uploadingAttachmentLineIds.delete(lineId);
      });
  }

  downloadLineAttachment(schemaId: string, line: WeightReportLineDto, attachment: WeightReportAttachmentDto): void {
    const workOrderId = this.workOrder?.id ?? '';
    const lineId = line?.id ?? '';
    if (!workOrderId || !schemaId || !lineId || !attachment?.id) {
      return;
    }

    this.weightReportService.downloadLineAttachment(workOrderId, schemaId, lineId, attachment.id).subscribe({
      next: (response) => {
        const downloadUrl = (response.body ?? '').toString().trim();
        if (!downloadUrl) {
          return;
        }

        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.rel = 'noopener noreferrer';
        anchor.target = '_blank';
        anchor.download = attachment.fileName || 'attachment';
        anchor.click();
      },
      error: () => {
        this.snackBar.open(this.translate.instant('weightReport.documentation.attachmentDownloadError'), 'OK', {
          duration: 5000,
        });
      },
    });
  }

  private buildLineRequest(): WeightLineRequest {
    return {
      clientLineId: this.editingLine?.clientLineId,
      performedBy: this.currentUserEmail || undefined,
      mainCategory: this.form.mainCategory,
      areaTagSystem: this.form.areaTagSystem || undefined,
      quantityMetres: this.form.quantityMetres || undefined,
      hoursSpent: this.form.hoursSpent ?? undefined,
      installedWeight: this.form.installedWeight ?? undefined,
      removedWeight: this.form.removedWeight ?? undefined,
      changeFollowUpRequired: this.form.changeFollowUpRequired,
      description: this.form.description || undefined,
      comment: this.form.comment || undefined,
      selectedCategoryIds: Array.from(this.selectedCategoryIds),
    };
  }

  private async resolveOpenSchemaId(): Promise<string> {
    if (this.openSchema?.id) {
      return this.openSchema.id;
    }

    const workOrderId = this.workOrder?.id ?? '';
    if (!workOrderId) {
      return '';
    }

    const schema = await this.weightReportService.getOpenSchema(workOrderId);
    if (schema) {
      this.openSchema = schema;
    }

    return schema?.id ?? '';
  }

  private async refresh(): Promise<void> {
    const workOrderId = this.workOrder?.id ?? '';
    if (!workOrderId) {
      return;
    }

    try {
      const schemas = await this.weightReportService.getSchemas(workOrderId);
      this.openSchema = schemas.find((schema) => schema.status === WeightReportSchemaDto.StatusEnum.Open) ?? null;
      this.sentSchemas = schemas
        .filter((schema) => schema.status === WeightReportSchemaDto.StatusEnum.Sent)
        .sort((a, b) => Date.parse(b.sentAt ?? '') - Date.parse(a.sentAt ?? ''));
      this.lineAttachments = {};
      this.hasPendingLineChanges = await this.weightReportService.hasPendingLineChanges(workOrderId);

      if (this.openSchema) {
        this.loadLineAttachmentsForSchema(this.openSchema);
      }
    } catch {
      this.openSchema = null;
      this.sentSchemas = [];
    }
  }

  private async uploadPendingAttachments(workOrderId: string, schemaId: string, lineId: string): Promise<void> {
    const files = [...this.pendingAttachmentFiles];
    this.pendingAttachmentFiles = [];

    try {
      await Promise.all(
        files.map((file) =>
          firstValueFrom(this.weightReportService.uploadLineAttachment(workOrderId, schemaId, lineId, file)),
        ),
      );
    } catch {
      this.snackBar.open(this.translate.instant('weightReport.documentation.attachmentUploadError'), 'OK', {
        duration: 5000,
      });
    }
  }

  private resolveRecipient(projectId: string): void {
    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        const username = (user?.username ?? '').toString().trim();
        if (!username) {
          return;
        }
        this.projectController
          .getAuthorizedProjects(username)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (projects) => {
              const project = (projects ?? []).find((p) => p.id === projectId);
              this.recipientEmail = project?.weightChangeRecipientEmail ?? '';
            },
            error: () => (this.recipientEmail = ''),
          });
      });
  }

  private loadCategories(projectId: string): void {
    this.weightReportService
      .getCategories(projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (categories) =>
          (this.categories = (categories ?? []).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))),
        error: () => (this.categories = []),
      });
  }

  private loadLineAttachmentsForSchema(schema: WeightReportSchemaDto): void {
    if (!schema?.id || !this.workOrder?.id) {
      return;
    }

    (schema.lines ?? []).forEach((line) => {
      if (line.id && !this.lineAttachments[line.id]) {
        this.loadLineAttachments(schema.id!, line);
      }
    });
  }

  private loadLineAttachments(schemaId: string, line: WeightReportLineDto): void {
    const workOrderId = this.workOrder?.id ?? '';
    const lineId = line?.id ?? '';
    if (!workOrderId || !schemaId || !lineId || !this.isOnline) {
      return;
    }

    this.loadingAttachmentLineIds.add(lineId);
    this.weightReportService.listLineAttachments(workOrderId, schemaId, lineId).subscribe({
      next: (attachments) => {
        this.lineAttachments[lineId] = attachments ?? [];
        this.loadingAttachmentLineIds.delete(lineId);
      },
      error: () => {
        this.lineAttachments[lineId] = [];
        this.loadingAttachmentLineIds.delete(lineId);
        this.snackBar.open(this.translate.instant('weightReport.documentation.attachmentLoadError'), 'OK', {
          duration: 5000,
        });
      },
    });
  }
}
